import React, { useState } from 'react';
import { Calculator, ArrowRight, ShieldCheck, Users, CheckCircle2 } from 'lucide-react';

interface TipPoolCalculatorProps {
  onGetStarted?: () => void;
  compact?: boolean;
}

export const TipPoolCalculator: React.FC<TipPoolCalculatorProps> = ({ onGetStarted, compact = false }) => {
  const [totalTips, setTotalTips] = useState<string>('2400');
  const [allocationModel, setAllocationModel] = useState<'Hours Worked' | 'Points' | 'Equal Split'>('Hours Worked');
  const [totalHours, setTotalHours] = useState<string>('120');
  const [employeeHours, setEmployeeHours] = useState<string>('30');
  const [totalPoints, setTotalPoints] = useState<string>('80');
  const [employeePoints, setEmployeePoints] = useState<string>('20');
  const [numEmployees, setNumEmployees] = useState<string>('6');
  const [employeeName, setEmployeeName] = useState<string>('Jane Doe');

  const tipsNum = parseFloat(totalTips) || 0;
  const totHoursNum = parseFloat(totalHours) || 1;
  const empHoursNum = parseFloat(employeeHours) || 0;
  const totPointsNum = parseFloat(totalPoints) || 1;
  const empPointsNum = parseFloat(employeePoints) || 0;
  const numEmpNum = parseFloat(numEmployees) || 1;

  const getShare = () => {
    switch (allocationModel) {
      case 'Hours Worked':
        return (((tipsNum) / (totHoursNum || 1)) * empHoursNum).toFixed(2);
      case 'Points':
        return (((tipsNum) / (totPointsNum || 1)) * empPointsNum).toFixed(2);
      case 'Equal Split':
        return (tipsNum / (numEmpNum || 1)).toFixed(2);
      default:
        return '0.00';
    }
  };

  const shareAmount = parseFloat(getShare()) || 0;
  const sharePercentage = tipsNum > 0 ? ((shareAmount / tipsNum) * 100).toFixed(1) : '0';
  const hourlyRate = totHoursNum > 0 ? (tipsNum / totHoursNum).toFixed(2) : '0.00';

  // Sample team breakdown to show real-world transparency
  const sampleTeam = [
    { name: 'Jane Doe', role: 'Lead Server', share: shareAmount, hours: empHoursNum, active: true },
    { 
      name: 'John Smith', 
      role: 'Bartender', 
      share: allocationModel === 'Hours Worked' 
        ? ((tipsNum / totHoursNum) * 28) 
        : allocationModel === 'Points' 
          ? ((tipsNum / totPointsNum) * 22) 
          : (tipsNum / numEmpNum),
      hours: 28,
      active: false
    },
    { 
      name: 'Alice Johnson', 
      role: 'Barback / Runner', 
      share: allocationModel === 'Hours Worked' 
        ? ((tipsNum / totHoursNum) * 20) 
        : allocationModel === 'Points' 
          ? ((tipsNum / totPointsNum) * 14) 
          : (tipsNum / numEmpNum),
      hours: 20,
      active: false
    },
  ];

  return (
    <div className={`bg-white rounded-3xl border border-gray-200/90 shadow-2xl overflow-hidden text-left flex flex-col justify-between ${compact ? 'p-5 sm:p-6' : 'p-6 sm:p-8'}`}>
      <div>
        {/* Card Header */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-4 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 shadow-sm">
              <Calculator size={18} />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-tight text-gray-900 flex items-center gap-1.5">
                Tip Pool Calculator
              </h3>
              <p className="text-[11px] text-gray-500 font-medium">Interactive Shift Allocation Simulator</p>
            </div>
          </div>
          <div className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-black uppercase tracking-wider border border-emerald-100">
            <ShieldCheck size={12} />
            FLSA Compliant
          </div>
        </div>

        {/* Inputs */}
        <div className="space-y-3.5">
          {/* Tip Pool and Model Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">
                Total Tip Pool ($)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-black text-xs">$</span>
                <input
                  type="number"
                  placeholder="e.g. 2400"
                  value={totalTips}
                  onChange={(e) => setTotalTips(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">
                Allocation Model
              </label>
              <select
                value={allocationModel}
                onChange={(e) => setAllocationModel(e.target.value as any)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all cursor-pointer"
              >
                <option value="Hours Worked">Hours Worked</option>
                <option value="Points">Points System</option>
                <option value="Equal Split">Equal Split</option>
              </select>
            </div>
          </div>

          {/* Conditional Fields based on model */}
          {allocationModel === 'Hours Worked' && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50/40 rounded-2xl border border-blue-100/60">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-blue-900 mb-1">
                  Total Hours
                </label>
                <input
                  type="number"
                  placeholder="e.g. 120"
                  value={totalHours}
                  onChange={(e) => setTotalHours(e.target.value)}
                  className="w-full px-3 py-1.5 bg-white border border-blue-200 rounded-xl text-xs font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-blue-900 mb-1">
                  Employee Hours
                </label>
                <input
                  type="number"
                  placeholder="e.g. 30"
                  value={employeeHours}
                  onChange={(e) => setEmployeeHours(e.target.value)}
                  className="w-full px-3 py-1.5 bg-white border border-blue-200 rounded-xl text-xs font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {allocationModel === 'Points' && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50/40 rounded-2xl border border-blue-100/60">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-blue-900 mb-1">
                  Total Points
                </label>
                <input
                  type="number"
                  placeholder="e.g. 80"
                  value={totalPoints}
                  onChange={(e) => setTotalPoints(e.target.value)}
                  className="w-full px-3 py-1.5 bg-white border border-blue-200 rounded-xl text-xs font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-blue-900 mb-1">
                  Employee Points
                </label>
                <input
                  type="number"
                  placeholder="e.g. 20"
                  value={employeePoints}
                  onChange={(e) => setEmployeePoints(e.target.value)}
                  className="w-full px-3 py-1.5 bg-white border border-blue-200 rounded-xl text-xs font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {allocationModel === 'Equal Split' && (
            <div className="p-3 bg-blue-50/40 rounded-2xl border border-blue-100/60">
              <label className="block text-[10px] font-black uppercase tracking-wider text-blue-900 mb-1">
                Number of Eligible Employees
              </label>
              <input
                type="number"
                placeholder="e.g. 6"
                value={numEmployees}
                onChange={(e) => setNumEmployees(e.target.value)}
                className="w-full px-3 py-1.5 bg-white border border-blue-200 rounded-xl text-xs font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Employee Selection */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">
              Select Staff Member
            </label>
            <select
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all cursor-pointer"
            >
              <option value="Jane Doe">Jane Doe (Lead Server)</option>
              <option value="John Smith">John Smith (Bartender)</option>
              <option value="Alice Johnson">Alice Johnson (Barback / Runner)</option>
            </select>
          </div>

          {/* Live Result Highlight Box */}
          <div className="p-4 bg-gradient-to-br from-blue-50 via-white to-emerald-50/40 rounded-2xl border border-blue-100 shadow-sm flex items-center justify-between">
            <div>
              <span className="text-[10px] font-black text-blue-700 uppercase tracking-widest block">
                {employeeName || 'Staff Member'} Payout
              </span>
              <div className="text-2xl font-black text-gray-900 tracking-tight mt-0.5">
                ${getShare()}
              </div>
              <div className="text-[10px] text-gray-500 font-semibold mt-0.5">
                {sharePercentage}% of ${tipsNum.toLocaleString()} pool {allocationModel === 'Hours Worked' ? `(Effective rate: $${hourlyRate}/hr)` : ''}
              </div>
            </div>
            <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 shadow-sm">
              <CheckCircle2 size={20} />
            </div>
          </div>

          {/* Mini Shift Distribution Preview */}
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-gray-400">
              <span className="flex items-center gap-1">
                <Users size={11} /> Shift Breakdown Sample
              </span>
              <span>Calculated Share</span>
            </div>
            <div className="space-y-1">
              {sampleTeam.map((member) => (
                <div
                  key={member.name}
                  className={`flex items-center justify-between px-2.5 py-1.5 rounded-xl text-xs transition-all ${
                    member.name === employeeName
                      ? 'bg-blue-50/80 border border-blue-200 font-black text-blue-900'
                      : 'bg-gray-50/60 border border-gray-100 text-gray-600 font-medium'
                  }`}
                >
                  <span className="text-[11px] truncate max-w-[180px]">
                    {member.name} <span className="text-[10px] text-gray-400">({member.role})</span>
                  </span>
                  <span className={`text-[11px] ${member.name === employeeName ? 'text-blue-700 font-black' : 'text-gray-800 font-bold'}`}>
                    ${member.share.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Card Footer CTA */}
      <div className="pt-4 border-t border-gray-100 mt-4 flex items-center justify-between gap-3">
        <div className="text-[10px] text-gray-400 font-semibold leading-tight">
          Automated shift reconciliation • No spreadsheet errors
        </div>
        {onGetStarted ? (
          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shadow-md shadow-blue-500/20 active:scale-[0.98] cursor-pointer shrink-0"
          >
            <span>Start Free</span>
            <ArrowRight size={11} />
          </button>
        ) : (
          <a
            href="#tools"
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-blue-600 hover:text-blue-700"
          >
            More Tools <ArrowRight size={11} />
          </a>
        )}
      </div>
    </div>
  );
};
